import type { Notification, NotificationCategory, UnreadCounts } from '@/api/notificationsApi'

/**
 * The categories the Notifications page offers as chips, and which backend
 * categories each one shows.
 *
 * Like notificationLink and notificationNavBadges, this knowledge lives in the
 * frontend: the backend tags each row with the event that produced it, and the
 * frontend alone decides how those events are grouped for a reader. A chip can
 * therefore take more than one backend category without a backend change.
 *
 * Pure (no React imports) so the grouping can be unit tested directly. The page
 * holds the icons, keyed on `id`.
 */

export interface NotificationCategoryGroup {
  /** The `?category=` value in the page URL. Stable: links are bookmarked. */
  id: string
  label: string
  categories: readonly NotificationCategory[]
}

// Chip order. Every backend category belongs to exactly one group (the test
// pins this), so the counts on the chips always add up to the total.
export const NOTIFICATION_CATEGORY_GROUPS: readonly NotificationCategoryGroup[] = [
  { id: 'patients', label: 'Patients', categories: ['PATIENT_INTAKE'] },
  { id: 'procedures', label: 'Procedures', categories: ['PROCEDURE_DUE'] },
  { id: 'lis', label: 'LIS', categories: ['LIS_RESULT_REVIEW'] },
  { id: 'instruments', label: 'Instrument results', categories: ['LAB_RESULT'] },
  // Field notification rules and out-of-specification alerts both arrive as
  // FORM_NOTIFICATION_RULE, so both show here.
  { id: 'forms', label: 'Form alerts', categories: ['FORM_NOTIFICATION_RULE'] },
  // Offline mobile actions refused or in conflict on sync (z8nrz7fh5c). The
  // chip is in place; nothing sends these notifications yet.
  { id: 'offline', label: 'Offline mobile actions', categories: ['OFFLINE_MOBILE_ACTION'] },
  // Title21 Support replies and status changes on this tenant's tickets.
  { id: 'support', label: 'Support', categories: ['SUPPORT_TICKET'] },
]

/** The group a `?category=` value names, or undefined for All (absent or unknown). */
export function categoryGroupFromParam(param: string | null): NotificationCategoryGroup | undefined {
  return param ? NOTIFICATION_CATEGORY_GROUPS.find((g) => g.id === param) : undefined
}

/** Unread count for a group; for All (no group) the total. */
export function groupUnreadCount(group: NotificationCategoryGroup | undefined, counts: UnreadCounts | undefined): number {
  if (!counts) return 0
  if (!group) return counts.count
  return group.categories.reduce((sum, c) => sum + (counts.byCategory[c] ?? 0), 0)
}

/** The group a notification belongs to. Undefined for a category this build does not know. */
export function groupOf(n: Pick<Notification, 'category'>): NotificationCategoryGroup | undefined {
  return NOTIFICATION_CATEGORY_GROUPS.find((g) => (g.categories as readonly string[]).includes(n.category))
}
